import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { getTransactions } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';

const TransactionList = () => {
  const navigate = useNavigate();
  const portal = usePortalScope();
  const salons = portal.salons;
  const brands = portal.brands;
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    brandId: '',
    salonId: '',
    branch: '',
    status: '',
    mobile: ''
  });

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const getTxParams = useCallback(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    Object.assign(params, tenantListParams(portal, filters));
    if (filters.branch) params.branch = filters.branch;
    if (filters.status) params.status = filters.status;
    if (filters.mobile) params.mobile = filters.mobile;
    return params;
  }, [filters, portal]);

  const { items: transactions, loading, loadingMore, error, pagination, loadMore } = useAdminInfiniteList({
    load: getTransactions,
    resetKey: filtersKey,
    getParams: getTxParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const listView = useAdminListView(transactions, (txn) => txn._id, {
    defaultView: 'table',
    resetKey: filtersKey
  });

  const transactionColumns = useMemo(
    () => [
      {
        key: 'transactionId',
        label: 'Transaction ID',
        render: (txn) => (
          <strong>#{txn.transactionId || txn._id.slice(-8).toUpperCase()}</strong>
        )
      },
      { key: 'date', label: 'Date', render: (txn) => formatDate(txn.createdAt) },
      { key: 'customer', label: 'Customer', render: (txn) => txn.user?.profile?.name || '-' },
      { key: 'mobile', label: 'Mobile', render: (txn) => txn.user?.mobile || '-' },
      { key: 'brand', label: 'Brand', render: (txn) => txn.cart?.salon?.brand?.name || '-' },
      {
        key: 'branch',
        label: 'Branch',
        render: (txn) => (
          <>
            {txn.cart?.salon?.name || '-'}
            {txn.cart?.salon?.branch && (
              <small style={{ display: 'block', color: '#666' }}>{txn.cart.salon.branch}</small>
            )}
          </>
        )
      },
      { key: 'amount', label: 'Amount', render: (txn) => `Rs. ${txn.amount?.toLocaleString()}` },
      {
        key: 'status',
        label: 'Status',
        render: (txn) => (
          <span
            className={`status-badge status-${txn.status} ${
              txn.status === 'completed' ? 'active' : ''
            }`}
          >
            {txn.status}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (txn) => (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => navigate(`/admin/transactions/${txn._id}`)}
          >
            View Details
          </button>
        )
      }
    ],
    [navigate]
  );

  const transactionListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && transactions.length > 0 && (
        <p className="admin-infinite-end">All rows loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      brandId: '',
      salonId: '',
      branch: '',
      status: '',
      mobile: ''
    });
  };

  const hasActiveFilters = Boolean(
    filters.brandId ||
      filters.salonId ||
      filters.branch ||
      filters.status ||
      filters.mobile ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.search
  );

  const transactionEmptyState = (
    <div className="admin-card empty-state">
      <p>No transactions found.</p>
      <p style={{ marginTop: '8px', fontSize: '13px', color: '#8189A5' }}>
        {hasActiveFilters
          ? 'Try clearing filters or widening the date range.'
          : 'Completed payments will appear here once customers check out.'}
      </p>
      {hasActiveFilters && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={handleClearFilters}>
          Clear filters
        </button>
      )}
    </div>
  );

  const branches = [...new Set(salons.map((s) => s.branch).filter(Boolean))];

  return (
    <AdminLayout
      pageTitle="Transactions"
      subtitle={
        pagination.total
          ? `Showing ${transactions.length} of ${pagination.total} transactions · scroll for more`
          : 'View and filter all transactions'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Transaction List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search transactions by ID, customer, service..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <div className="form-group">
              <label>Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
            />
            <div className="form-group">
              <label title="Optional text label on a branch. Use Branch to pick a specific branch.">
                Location name
              </label>
              <select
                value={filters.branch}
                onChange={(e) => handleFilterChange('branch', e.target.value)}
              >
                <option value="">All location names</option>
                {branches.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input
                type="text"
                value={filters.mobile}
                onChange={(e) => handleFilterChange('mobile', e.target.value)}
                placeholder="Search by mobile"
              />
            </div>
            <div className="form-group filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="admin-card empty-state">
            <p>Could not load transactions. Please try again.</p>
          </div>
        ) : loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={transactions.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={transactions}
              getItemId={(txn) => txn._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={transactionColumns}
              tableClassName="transaction-table"
              renderGridCard={(txn) => (
                <div className="admin-card admin-card--stretch transaction-card">
                  <div className="transaction-header">
                    <div>
                      <h3>#{txn.transactionId || txn._id.slice(-8).toUpperCase()}</h3>
                      <p className="transaction-date">{formatDate(txn.createdAt)}</p>
                    </div>
                    <span
                      className={`status-badge status-${txn.status} ${
                        txn.status === 'completed' ? 'active' : ''
                      }`}
                    >
                      {txn.status}
                    </span>
                  </div>
                  <div className="transaction-details">
                    <p>
                      <strong>Customer:</strong>{' '}
                      <span className="transaction-detail-value">{txn.user?.profile?.name || '-'}</span>
                    </p>
                    <p>
                      <strong>Mobile:</strong>{' '}
                      <span className="transaction-detail-value">{txn.user?.mobile || '-'}</span>
                    </p>
                    <p>
                      <strong>Brand:</strong>{' '}
                      <span className="transaction-detail-value">{txn.cart?.salon?.brand?.name || '-'}</span>
                    </p>
                    <p>
                      <strong>Branch:</strong>{' '}
                      <span className="transaction-detail-value">
                        {txn.cart?.salon?.name || '-'}
                        {txn.cart?.salon?.branch ? ` (${txn.cart.salon.branch})` : ''}
                      </span>
                    </p>
                    <p>
                      <strong>Amount:</strong>{' '}
                      <span className="transaction-detail-value">
                        Rs. {txn.amount?.toLocaleString() ?? '-'}
                      </span>
                    </p>
                  </div>
                  <div className="transaction-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/admin/transactions/${txn._id}`)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              )}
              emptyState={transactionEmptyState}
              scrollRef={scrollRef}
              footer={transactionListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default TransactionList;
